import { api } from './api';

/**
 * Web-Push im Browser: Abonnieren/Abmelden des aktuellen Geräts.
 * Voraussetzung: registrierter Service Worker (Produktions-Build bzw. VITE_PWA_DEV im Dev-Modus).
 */

export type PushSupport = 'supported' | 'unsupported' | 'ios-needs-install' | 'insecure';

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported';
  if (!window.isSecureContext) return 'insecure';
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  // iOS/iPadOS (ab 16.4): Web-Push nur für zum Home-Bildschirm hinzugefügte Apps
  if (ios && !standalone) return 'ios-needs-install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return 'supported';
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Aktive Service-Worker-Registrierung (ohne auf Registrierung zu warten, falls keine existiert). */
export async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return null;
  if (reg.active) return reg;
  // Registrierung läuft gerade – kurz auf Aktivierung warten
  return Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), 5000))]);
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export class PushSetupError extends Error {}

/** Berechtigung anfragen, Gerät beim Push-Dienst abonnieren und beim Server registrieren. */
export async function subscribePush(): Promise<PushSubscription> {
  const reg = await getRegistration();
  if (!reg) throw new PushSetupError('Der Service Worker ist nicht aktiv. Bitte laden Sie die Seite neu.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushSetupError(
      permission === 'denied'
        ? 'Benachrichtigungen wurden im Browser blockiert. Bitte erlauben Sie sie in den Website-Einstellungen.'
        : 'Die Berechtigung für Benachrichtigungen wurde nicht erteilt.',
    );
  }
  const { publicKey } = await api.get<{ publicKey: string }>('/push/public-key');
  const key = urlBase64ToUint8Array(publicKey);
  let sub = await reg.pushManager.getSubscription();
  // Abo mit anderem Server-Schlüssel (z. B. nach Neuinstallation) ersetzen
  if (sub) {
    const existing = sub.options.applicationServerKey;
    const same = existing && new Uint8Array(existing).every((b, i) => b === key[i]) && new Uint8Array(existing).length === key.length;
    if (!same) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  const json = sub.toJSON();
  await api.post('/push/subscribe', { endpoint: sub.endpoint, keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth } });
  return sub;
}

/** Gerät abmelden (Server + Push-Dienst). */
export async function unsubscribePush(): Promise<void> {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  try {
    await api.post('/push/unsubscribe', { endpoint: sub.endpoint });
  } finally {
    await sub.unsubscribe().catch(() => undefined);
  }
}
